import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { PlusIcon, TrashIcon, SaveIcon, CheckCircleIcon, ArrowLeftIcon, PencilIcon, InfoIcon } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
// --- Mock Historical Data ---
const historicalData: Record<string, {
  item: string;
  type: string;
  plannedQty: number;
  actualQty: number;
  planned: number;
  actual: number;
}[]> = {
  'FY 2024/25': [{
    item: 'Laptop Pro X',
    type: 'Goods',
    plannedQty: 150,
    actualQty: 160,
    planned: 180000,
    actual: 195000
  }, {
    item: 'A4 Printer Paper',
    type: 'Goods',
    plannedQty: 1200,
    actualQty: 1160,
    planned: 30000,
    actual: 29000
  }, {
    item: 'Ink Cartridges HP 61XL',
    type: 'Goods',
    plannedQty: 300,
    actualQty: 340,
    planned: 15000,
    actual: 17000
  }, {
    item: 'Warehouse Expansion',
    type: 'Works',
    plannedQty: 1,
    actualQty: 1,
    planned: 200000,
    actual: 150000
  }, {
    item: 'Office Renovation',
    type: 'Works',
    plannedQty: 1,
    actualQty: 1,
    planned: 90000,
    actual: 92000
  }, {
    item: 'IT Support Contract',
    type: 'Service',
    plannedQty: 1,
    actualQty: 1,
    planned: 40000,
    actual: 42000
  }, {
    item: 'Security Services',
    type: 'Service',
    plannedQty: 1,
    actualQty: 1,
    planned: 35000,
    actual: 34000
  }, {
    item: 'Cleaning Services',
    type: 'Service',
    plannedQty: 1,
    actualQty: 1,
    planned: 35000,
    actual: 34000
  }],
  'FY 2023/24': [{
    item: 'Laptop Pro X',
    type: 'Goods',
    plannedQty: 120,
    actualQty: 118,
    planned: 150000,
    actual: 145000
  }, {
    item: 'Office Chairs',
    type: 'Goods',
    plannedQty: 50,
    actualQty: 44,
    planned: 40000,
    actual: 35000
  }, {
    item: 'A4 Printer Paper',
    type: 'Goods',
    plannedQty: 1000,
    actualQty: 1120,
    planned: 25000,
    actual: 28000
  }, {
    item: 'Road Construction',
    type: 'Works',
    plannedQty: 1,
    actualQty: 1,
    planned: 500000,
    actual: 480000
  }, {
    item: 'Building Maintenance',
    type: 'Works',
    plannedQty: 1,
    actualQty: 1,
    planned: 60000,
    actual: 75000
  }, {
    item: 'HR Consulting',
    type: 'Service',
    plannedQty: 1,
    actualQty: 1,
    planned: 80000,
    actual: 85000
  }, {
    item: 'Fleet Management',
    type: 'Service',
    plannedQty: 1,
    actualQty: 1,
    planned: 120000,
    actual: 120000
  }, {
    item: 'Safety Training',
    type: 'Service',
    plannedQty: 1,
    actualQty: 1,
    planned: 30000,
    actual: 32000
  }]
};
// Mock existing plans (for view/edit mode)
const mockExistingPlans: Record<string, {
  fiscalYear: string;
  status: string;
  items: PlanItem[];
}> = {
  'plan-2025-26': {
    fiscalYear: 'FY 2025/26',
    status: 'Draft',
    items: [{
      id: '1',
      type: 'Goods',
      productId: '1',
      category: 'IT Equipment',
      department: 'IT',
      description: 'Laptop Pro X',
      qty: 20,
      unitPrice: 1200,
      method: 'Direct Purchase',
      priority: 'High',
      quarter: 'Q1',
      remarks: 'Standard spec'
    }, {
      id: '2',
      type: 'Goods',
      productId: '2',
      category: 'Office Supplies',
      department: 'Admin',
      description: 'A4 Printer Paper',
      qty: 100,
      unitPrice: 25,
      method: 'Short Purchase',
      priority: 'Low',
      quarter: 'Q1',
      remarks: ''
    }, {
      id: '3',
      type: 'Works',
      category: 'Construction',
      department: 'Facilities',
      description: 'Warehouse expansion Phase 2',
      qty: 1,
      unitPrice: 350000,
      method: 'Open Tender',
      priority: 'High',
      quarter: 'Q2',
      remarks: 'Continuation from FY 24/25'
    }, {
      id: '4',
      type: 'Service',
      category: 'Services',
      department: 'HR',
      description: 'Annual IT support contract',
      qty: 1,
      unitPrice: 60000,
      method: 'Direct Purchase',
      priority: 'Medium',
      quarter: 'Q1',
      remarks: 'Renewal'
    }]
  },
  'plan-2024-25': {
    fiscalYear: 'FY 2024/25',
    status: 'Approved',
    items: [{
      id: '1',
      type: 'Goods',
      productId: '1',
      category: 'IT Equipment',
      department: 'IT',
      description: 'Laptop Pro X',
      qty: 5,
      unitPrice: 1200,
      method: 'Open Tender',
      priority: 'High',
      quarter: 'Q1',
      remarks: ''
    }, {
      id: '2',
      type: 'Goods',
      productId: '3',
      category: 'Office Supplies',
      department: 'Admin',
      description: 'Ink Cartridges HP 61XL',
      qty: 200,
      unitPrice: 35,
      method: 'Short Purchase',
      priority: 'Medium',
      quarter: 'Q2',
      remarks: ''
    }]
  }
};
import { mockProducts } from '../data/mockData';
const categories = ['IT Equipment', 'Office Supplies', 'Construction', 'Services', 'Vehicles', 'Furniture', 'Safety Eq.', 'Maintenance'];
const departments = ['IT', 'Admin', 'Facilities', 'HR', 'Logistics', 'Operations'];
const methods = ['Direct Purchase', 'Short Purchase', 'Open Tender'];
const priorityOptions = ['High', 'Medium', 'Low'];
const quarterOptions = ['Q1', 'Q2', 'Q3', 'Q4'];
const referenceYears = ['FY 2024/25', 'FY 2023/24'];
const planTypes = ['Goods', 'Works', 'Service'] as const;
type PlanType = (typeof planTypes)[number];
interface PlanItem {
  id: string;
  type: PlanType;
  productId?: string;
  category: string;
  department: string;
  description: string;
  qty: number;
  unitPrice: number;
  method: string;
  priority: string;
  quarter: string;
  remarks: string;
}
export function AnnualProcurementPlan() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isEditRoute = location.pathname.endsWith('/edit');
  const isCreateMode = !id;
  const existingPlan = id ? mockExistingPlans[id] : null;
  const isViewMode = !!id && !isEditRoute;
  const isEditable = isCreateMode || isEditRoute;
  // Form state
  const [planFiscalYear, setPlanFiscalYear] = useState(existingPlan?.fiscalYear || 'FY 2025/26');
  const [currentPlan, setCurrentPlan] = useState<PlanItem[]>(existingPlan?.items || []);
  // Reference year for historical analysis
  const [refYear, setRefYear] = useState('FY 2024/25');
  const [refExpanded, setRefExpanded] = useState(true);
  const histData = historicalData[refYear] || [];
  // Historical calculations
  const totalPlanned = histData.reduce((sum, item) => sum + item.planned, 0);
  const totalActual = histData.reduce((sum, item) => sum + item.actual, 0);
  const variance = totalActual - totalPlanned;
  const variancePercent = totalPlanned ? variance / totalPlanned * 100 : 0;
  const utilization = totalPlanned ? totalActual / totalPlanned * 100 : 0;
  // Current plan calculations
  const currentTotal = currentPlan.reduce((sum, item) => sum + item.qty * item.unitPrice, 0);
  const highCount = currentPlan.filter(i => i.priority === 'High').length;
  const medCount = currentPlan.filter(i => i.priority === 'Medium').length;
  const lowCount = currentPlan.filter(i => i.priority === 'Low').length;
  const handleAddRow = () => {
    setCurrentPlan([...currentPlan, {
      id: Date.now().toString(),
      type: 'Goods',
      productId: '',
      category: '',
      department: '',
      description: '',
      qty: 1,
      unitPrice: 0,
      method: 'Direct Purchase',
      priority: 'Medium',
      quarter: 'Q1',
      remarks: ''
    }]);
  };
  const handleProductSelect = (rowId: string, productId: string) => {
    const product = mockProducts.find(p => p.id === productId);
    if (product) {
      setCurrentPlan(currentPlan.map(item => item.id === rowId ? {
        ...item,
        productId: product.id,
        description: product.productName,
        unitPrice: product.defaultRate || 0,
        category: product.subCategory || item.category
      } : item));
    } else {
      setCurrentPlan(currentPlan.map(item => item.id === rowId ? {
        ...item,
        productId: '',
        description: '',
        unitPrice: 0
      } : item));
    }
  };
  const handleTypeChange = (rowId: string, newType: PlanType) => {
    setCurrentPlan(currentPlan.map(item => item.id === rowId ? {
      ...item,
      type: newType,
      productId: '',
      description: '',
      unitPrice: 0
    } : item));
  };
  const handleRemoveRow = (rowId: string) => {
    setCurrentPlan(currentPlan.filter(item => item.id !== rowId));
  };
  const handleUpdateRow = (rowId: string, field: keyof PlanItem, value: any) => {
    setCurrentPlan(currentPlan.map(item => item.id === rowId ? {
      ...item,
      [field]: value
    } : item));
  };
  const pageTitle = isCreateMode ? 'Create Annual Procurement Plan' : isEditRoute ? 'Edit Annual Procurement Plan' : 'View Annual Procurement Plan';
  return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-527">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-528">
        <div className="flex items-center space-x-4" data-id="element-529">
          <button onClick={() => navigate('/procurement-plan')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-530">
            <ArrowLeftIcon className="w-5 h-5" data-id="element-531" />
          </button>
          <div data-id="element-532">
            <div className="flex items-center space-x-3" data-id="element-533">
              <h1 className="text-2xl font-bold text-gray-900 tracking-tight" data-id="element-534">
                {pageTitle}
              </h1>
              {existingPlan && <StatusBadge status={existingPlan.status} data-id="element-535" />}
            </div>
            <p className="text-sm text-gray-500 mt-1" data-id="element-536">
              {isCreateMode ? 'Review previous year performance and create a new procurement plan.' : `Fiscal Year: ${existingPlan?.fiscalYear || planFiscalYear}`}
            </p>
          </div>
        </div>
        {isViewMode && existingPlan?.status === 'Draft' && <button onClick={() => navigate(`/procurement-plan/${id}/edit`)} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors" data-id="element-537">
            <PencilIcon className="w-4 h-4 mr-2" data-id="element-538" />
            Edit Plan
          </button>}
      </div>

      {/* Fiscal Year Selection (Create/Edit only) */}
      {isEditable && <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5" data-id="element-539">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-540">
            <div data-id="element-541">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-542">
                Plan Fiscal Year
              </label>
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm border p-2" value={planFiscalYear} onChange={e => setPlanFiscalYear(e.target.value)} data-id="element-543">
                <option value="FY 2025/26" data-id="element-544">FY 2025/26</option>
                <option value="FY 2026/27" data-id="element-545">FY 2026/27</option>
              </select>
            </div>
          </div>
        </div>}

      {/* ===== SECTION B: Plan Line Items ===== */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-546">
        {/* Summary Bar */}
        <div className="px-6 py-4 border-b border-gray-200 bg-indigo-50 flex flex-wrap items-center justify-between gap-4" data-id="element-547">
          <div className="flex items-center space-x-8" data-id="element-548">
            <div data-id="element-549">
              <p className="text-xs font-medium text-indigo-800 uppercase" data-id="element-550">
                Total Allocated
              </p>
              <p className="text-xl font-bold text-indigo-900" data-id="element-551">
                ${currentTotal.toLocaleString()}
              </p>
            </div>
            <div data-id="element-552">
              <p className="text-xs font-medium text-indigo-800 uppercase" data-id="element-553">
                Items
              </p>
              <p className="text-xl font-bold text-indigo-900" data-id="element-554">
                {currentPlan.length}
              </p>
            </div>
            <div className="flex items-center space-x-3 text-xs font-medium" data-id="element-555">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-red-100 text-red-700" data-id="element-556">
                {highCount} High
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-700" data-id="element-557">
                {medCount} Med
              </span>
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700" data-id="element-558">
                {lowCount} Low
              </span>
            </div>
          </div>
          {isEditable && <div className="flex space-x-3" data-id="element-559">
              <button onClick={handleAddRow} className="inline-flex items-center px-3 py-1.5 border border-indigo-200 text-sm font-medium rounded-md text-indigo-700 bg-white hover:bg-indigo-50 transition-colors" data-id="element-560">
                <PlusIcon className="w-4 h-4 mr-1" data-id="element-561" /> Add Item
              </button>
              <button className="inline-flex items-center px-3 py-1.5 border border-indigo-200 text-sm font-medium rounded-md text-indigo-700 bg-white hover:bg-indigo-50 transition-colors" data-id="element-562">
                <SaveIcon className="w-4 h-4 mr-1" data-id="element-563" /> Save Draft
              </button>
              <button className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 transition-colors shadow-sm" data-id="element-564">
                <CheckCircleIcon className="w-4 h-4 mr-1" data-id="element-565" /> Submit Plan
              </button>
            </div>}
        </div>

        {/* Table */}
        <div className="overflow-x-auto" data-id="element-566">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-567">
            <thead className="bg-gray-50" data-id="element-568">
              <tr data-id="element-569">
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-28" data-id="element-570">
                  Type
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase min-w-[200px]" data-id="element-571">
                  Item
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase w-20" data-id="element-572">
                  Qty
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase w-28" data-id="element-573">
                  Unit Price
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase w-28" data-id="element-574">
                  Total
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase w-24" data-id="element-575">
                  Priority
                </th>
                {isEditable && <th className="px-3 py-3 w-12" data-id="element-576"></th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200" data-id="element-577">
              {currentPlan.map(item => {
              const typeColor = item.type === 'Goods' ? 'bg-blue-100 text-blue-800' : item.type === 'Works' ? 'bg-orange-100 text-orange-800' : 'bg-purple-100 text-purple-800';
              const historicalItem = histData.find(h => h.item.toLowerCase() === item.description.toLowerCase());
              return <tr key={item.id} className="hover:bg-gray-50" data-id="element-578">
                    {/* Type */}
                    <td className="px-3 py-2" data-id="element-579">
                      {isEditable ? <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm border p-1.5 font-medium" value={item.type} onChange={e => handleTypeChange(item.id, e.target.value as PlanType)} data-id="element-580">
                          {planTypes.map(t => <option key={t} value={t} data-id="element-581">
                              {t}
                            </option>)}
                        </select> : <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${typeColor}`} data-id="element-582">
                          {item.type}
                        </span>}
                    </td>
                    {/* Item */}
                    <td className="px-3 py-2" data-id="element-583">
                      <div className="flex items-center space-x-2" data-id="element-584">
                        <div className="flex-1" data-id="element-585">
                          {isEditable ? item.type === 'Goods' ? <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm border p-1.5" value={item.productId || ''} onChange={e => handleProductSelect(item.id, e.target.value)} data-id="element-586">
                                <option value="" data-id="element-587">Select product...</option>
                                {mockProducts.filter(p => p.status === 'Active' && (p.id === item.productId || !currentPlan.some(row => row.type === 'Goods' && row.productId === p.id))).map(p => <option key={p.id} value={p.id} data-id="element-588">
                                      {p.productName} ({p.productCode})
                                    </option>)}
                              </select> : <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm border p-1.5" value={item.description} onChange={e => handleUpdateRow(item.id, 'description', e.target.value)} placeholder={item.type === 'Works' ? 'Describe works...' : 'Describe service...'} data-id="element-589" /> : <span className="text-sm text-gray-900" data-id="element-590">
                              {item.description}
                            </span>}
                        </div>
                        {historicalItem && <div className="relative group flex-shrink-0" data-id="element-591">
                            <div className="w-6 h-6 rounded-full bg-indigo-100 border border-indigo-300 flex items-center justify-center cursor-help hover:bg-indigo-200 transition-colors" data-id="element-592">
                              <InfoIcon className="w-3.5 h-3.5 text-indigo-600" data-id="element-593" />
                            </div>
                            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-56 bg-gray-900 text-white text-xs rounded p-3 shadow-lg z-10" data-id="element-594">
                              <p className="font-semibold mb-2 border-b border-gray-700 pb-1" data-id="element-595">
                                Previous Year ({refYear})
                              </p>
                              <div className="space-y-1" data-id="element-596">
                                <div className="flex justify-between" data-id="element-597">
                                  <span className="text-gray-400" data-id="element-598">
                                    Planned Qty:
                                  </span>
                                  <span className="font-medium" data-id="element-599">
                                    {historicalItem.plannedQty.toLocaleString()}
                                  </span>
                                </div>
                                <div className="flex justify-between" data-id="element-600">
                                  <span className="text-gray-400" data-id="element-601">
                                    Actual Qty:
                                  </span>
                                  <span className="font-medium" data-id="element-602">
                                    {historicalItem.actualQty.toLocaleString()}
                                  </span>
                                </div>
                              </div>
                              {/* Tooltip arrow */}
                              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-gray-900" data-id="element-603"></div>
                            </div>
                          </div>}
                      </div>
                    </td>
                    {/* Qty */}
                    <td className="px-3 py-2 text-right" data-id="element-604">
                      {item.type === 'Goods' ? isEditable ? <input type="number" min="1" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm border p-1.5 text-right" value={item.qty} onChange={e => handleUpdateRow(item.id, 'qty', Number(e.target.value))} data-id="element-605" /> : <span className="text-sm text-gray-900" data-id="element-606">
                            {item.qty}
                          </span> : <span className="text-sm text-gray-400" data-id="element-607">—</span>}
                    </td>
                    {/* Unit Price */}
                    <td className="px-3 py-2 text-right" data-id="element-608">
                      {isEditable ? <input type="number" min="0" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm border p-1.5 text-right" value={item.unitPrice} onChange={e => handleUpdateRow(item.id, 'unitPrice', Number(e.target.value))} data-id="element-609" /> : <span className="text-sm text-gray-900" data-id="element-610">
                          ${item.unitPrice.toLocaleString()}
                        </span>}
                    </td>
                    {/* Total */}
                    <td className="px-3 py-2 text-right text-sm font-medium text-gray-900" data-id="element-611">
                      $
                      {(item.type === 'Goods' ? item.qty * item.unitPrice : item.unitPrice).toLocaleString()}
                    </td>
                    {/* Priority */}
                    <td className="px-3 py-2" data-id="element-612">
                      {isEditable ? <select className={`block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm border p-1.5 font-medium ${item.priority === 'High' ? 'text-red-600' : item.priority === 'Medium' ? 'text-amber-600' : 'text-green-600'}`} value={item.priority} onChange={e => handleUpdateRow(item.id, 'priority', e.target.value)} data-id="element-613">
                          {priorityOptions.map(p => <option key={p} value={p} data-id="element-614">
                              {p}
                            </option>)}
                        </select> : <span className={`text-sm font-medium ${item.priority === 'High' ? 'text-red-600' : item.priority === 'Medium' ? 'text-amber-600' : 'text-green-600'}`} data-id="element-615">
                          {item.priority}
                        </span>}
                    </td>

                    {isEditable && <td className="px-3 py-2 text-center" data-id="element-616">
                        <button onClick={() => handleRemoveRow(item.id)} className="text-gray-400 hover:text-red-600 transition-colors p-1" data-id="element-617">
                          <TrashIcon className="w-4 h-4" data-id="element-618" />
                        </button>
                      </td>}
                  </tr>;
            })}
              {currentPlan.length === 0 && <tr data-id="element-619">
                  <td colSpan={isEditable ? 8 : 7} className="px-4 py-8 text-center text-sm text-gray-500" data-id="element-620">
                    {isEditable ? 'No items added yet. Click "Add Item" to start building the plan.' : 'No items in this plan.'}
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}